import type { RegenerateTeamsPermissionsIn } from '@repo/api-codegen';
import {
	fetchApiSuperuserGetWorkspace,
	fetchUpdateAllowedDomains,
	fetchUpdateDisabled,
} from '@repo/api-codegen';
import { isEmpty, isNil, pickBy } from 'lodash-es';
import { queryClient } from '../../../api';
import { apiClient, getEndpoints } from '../../../api/common';
import { INTERNAL_QUERY_ACTIONS_NAMESPACE } from '../../../api/hooks/internalQueryActions';
import { LINEAGE_QUERY_OPTIONS } from '../../../hooks/useLineage/constants';

export interface IFetchLineageParams {
	from_entity_id?: string;
	to_entity_id?: string;
	from_entity__type?: string;
	to_entity__type?: string;
	from_entity__native_type?: string;
	to_entity__native_type?: string;
	from_entity__integration_id?: string;
	to_entity__integration_id?: string;
	direction?: string;
	is_manual?: boolean;
	archived?: boolean;
	workspace_id?: string;
	integration_id?: string;
}

export interface IFetchResourceParams {
	id?: string;
	title?: string;
	entity_type?: string;
	native_type?: string;
	integration_id?: string;
	integration__type?: string;
	workspace_id?: string;
	stale?: boolean;
	archived?: boolean;
}

export interface IFetchWorkspaceParams {
	workspace_id?: string;
	name?: string;
}

export interface IUpdateAllowedDomainsParams {
	workspace_id: string;
	allowed_domains: string;
	totp: string;
}

export interface IUpdateDisabledParams {
	workspace_id: string;
	disabled: boolean;
	totp: string;
}

export interface IRegenerateWorkspacePoliciesParams {
	workspace_id: string;
	complete_rebuild: boolean;
}

export interface IUpdateWorkspaceQualityFlagsParams {
	workspace_id: string;
}

export interface IResetWorkspaceDqs2PoliciesParams {
	workspace_id: string;
	totp: string;
}

export interface ITestTunnelConnectivityParams {
	ssh_tunnel_id: string;
	target_hostname: string;
	target_port: number;
	timeout_seconds: number;
	totp: string;
}

export interface ITestConnectivityParams {
	integration_id: string;
	override_json: string;
	superuser_totp: string;
}

export interface IBulkExportParams {
	workspace_id: string;
	entity_type: string;
	email?: string;
	totp: string;
}

const filterParams = (params: object) =>
	pickBy(params, (value) => {
		// Don't filter out boolean values (including false)
		if (typeof value === 'boolean') {
			return true;
		}
		// Filter out null, undefined, and empty strings
		return !isNil(value) && !isEmpty(value);
	});

async function fetchQueryWithType(
	path: string,
	params: IFetchLineageParams | IFetchResourceParams | IFetchWorkspaceParams,
	options = LINEAGE_QUERY_OPTIONS
) {
	const queryKey = [...INTERNAL_QUERY_ACTIONS_NAMESPACE, path, params];
	const filters = filterParams(params);

	if (isEmpty(filters)) {
		return Promise.resolve([]);
	}

	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([path]);
	const { data } = await queryClient.fetchQuery({
		queryKey,
		queryFn: async () => await apiClient.post(url, filters),
		...options,
	});

	return path.includes('count') ? data : data.results;
}

export const fetchLineage = (params: IFetchLineageParams) =>
	fetchQueryWithType('lineage', params);

export const fetchLineageCount = (params: IFetchLineageParams) =>
	fetchQueryWithType('lineage_count', params);

export const fetchResource = (params: IFetchResourceParams) =>
	fetchQueryWithType('resource', params);

export const fetchResourceCount = (params: IFetchResourceParams) =>
	fetchQueryWithType('resource_count', params);

export const fetchWorkspace = (params: IFetchWorkspaceParams) =>
	fetchApiSuperuserGetWorkspace({
		queryParams: {
			search_term: params.workspace_id,
		},
	});

export const updateAllowedDomains = (params: IUpdateAllowedDomainsParams) =>
	fetchUpdateAllowedDomains({
		body: params,
	});

export const updateDisabled = (params: IUpdateDisabledParams) =>
	fetchUpdateDisabled({
		body: params,
	});

export const regenerateWorkspacePolicies = async (
	params: IRegenerateWorkspacePoliciesParams
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'regenerate_workspace_policies',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export const updateWorkspaceQualityFlags = async (
	params: IUpdateWorkspaceQualityFlagsParams
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'update_workspace_quality_flags',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export const resetWorkspaceDqs2Policies = async (
	params: IResetWorkspaceDqs2PoliciesParams
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'reset_dqs2_policies',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export const testTunnelConnectivity = async (
	params: ITestTunnelConnectivityParams
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'test_tunnel_connectivity',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export const testConnectivity = async (params: ITestConnectivityParams) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'test_connectivity',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export const bulkExport = async (params: IBulkExportParams) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'bulk_export',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export const regenerateTeamsPermissions = async (
	params: RegenerateTeamsPermissionsIn
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'regenerate_teams_permissions',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export interface IRemoveDisabledUsersFromTeamsParams {
	workspace_id: string;
}

export const removeDisabledUsersFromTeams = async (
	params: IRemoveDisabledUsersFromTeamsParams
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'remove_disabled_users_from_teams',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};

export interface GenerateWorkspaceLineageParams {
	workspace_id: string;
}

export const generateWorkspaceLineage = async (
	params: GenerateWorkspaceLineageParams
) => {
	const url = getEndpoints(INTERNAL_QUERY_ACTIONS_NAMESPACE).byPath([
		'generate_workspace_lineage',
	]);
	const { data } = await apiClient.post(url, params);
	return data;
};
